<template>
  <main class="puzzle game">
    <template v-if="isGameInitialized && !isGameFinished">
      <section>
        <div class="top_bar">
          <add-on-game-timer
            ref="timerRef"
            :initial-timer-in-seconds="timerForPuzzleGame"
            @time:expired="handleTimeExpired"
          />
        </div>
      </section>

      <section class="game_box">
        <div id="container">
          <div class="box_puzzle">
            <div v-if="!isGameInProgress" class="me full"></div>
          </div>

          <div class="pre_img prevent_click">
            <li data-bid="0">
              <img src="" alt="preview" />
            </li>
          </div>
        </div>

        <base-button
          v-if="isStartGameButtonVisible"
          container-class="start"
          @button:click="handleStartGame"
        >
          {{ getTranslations.page_puzzle_button_play }}
        </base-button>
      </section>
    </template>

    <base-loader-modal v-if="isLoaderModalOpen" />

    <game-maintenance
      v-else-if="isGameMaintenanceModalOpen"
      @redirect:page="handleGameMaintenanceRedirection"
    />

    <post-game-puzzle-modal
      v-else-if="isPostGameModalOpen"
      :successful="postGameInfo.successful"
      :prize-value="postGameInfo.prizeValue"
    />
  </main>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import $ from 'jquery';
import BaseButton from '../../../common/button/BaseButton.vue';
import BaseLoaderModal from '../../../common/modal/BaseLoaderModal.vue';
import GameMaintenance from '../../modals/Maintenance.vue';
import AddOnGameTimer from './components/AddOnGameTimer.vue';
import { addOnGameStyleMixin } from '../../../../common/mixins/addOnGameStyle';
import modals from '../../../../common/modals';
import { generateRandomNumberInRange, isPositiveNumber } from '../../../../helpers/common';
import PostGamePuzzleModal from './components/PostGamePuzzleModal.vue';

const imageList = [...Array(7).keys()].map((id) => {
  const filename = (id + 1).toString().padStart(2, '0');

  return `/static/images/puzzle/${ filename }.jpg`;
});

const originalTilePositionList = [0, 1, 2, 3, 4, 5, 6, 7, 8];

export default {
  name: 'AddOnGamePuzzle',
  components: { PostGamePuzzleModal, BaseLoaderModal, GameMaintenance, BaseButton, AddOnGameTimer },
  mixins: [addOnGameStyleMixin],
  data () {
    return {
      isGameInitialized: false,
      isGameInProgress: false,
      isGameFinished: false,
      isLoaderModalOpen: false,
      isGameMaintenanceModalOpen: false,
      isPostGameModalOpen: false,
      selectedImage: '',
      shuffledTilePositionList: [...originalTilePositionList],
      postGameInfo: { successful: false, prizeValue: null },
    };
  },
  mounted () {
    if (isPositiveNumber(this.timerForPuzzleGame)) {
      this.initGame();
    } else {
      this.openMaintenanceModal();
    }
  },
  computed: {
    ...mapState('common', [
      'timerForPuzzleGame',
      'addOnGameConfig',
    ]),
    ...mapGetters('i18n', [
      'getTranslations',
    ]),
    isStartGameButtonVisible () {
      return !this.isGameInProgress && !this.isGameFinished;
    },
  },
  methods: {
    ...mapActions('profile', [
      'getUserLoginDetails',
    ]),
    ...mapActions('game', [
      'finishAddOnGame',
    ]),
    ...mapMutations('common', [
      'resetAddOnGameConfig',
    ]),
    redirectToHomePage () {
      this.$router.push('/home');
    },
    openMaintenanceModal () {
      this.isGameMaintenanceModalOpen = true;

      modals.setupCommonModal({
        hideImmediate: true,
        afterHideCallback: () => {
          this.isGameMaintenanceModalOpen = false;

          this.redirectToHomePage();
        },
      });
    },
    closeLoaderModalAsPromise () {
      return new Promise(resolve => {
        modals.hideCommonModal({
          immediate: true,
          afterHideCallback: () => {
            this.isLoaderModalOpen = false;
            resolve();
          },
        });
      });
    },
    isPuzzleSolved () {
      return JSON.stringify(originalTilePositionList) === JSON.stringify(this.shuffledTilePositionList);
    },
    async initGame () {
      const selectedImageIndex = generateRandomNumberInRange(0, imageList.length - 1);

      this.selectedImage = imageList[selectedImageIndex];

      if (!this.selectedImage) {
        this.openMaintenanceModal();

        return;
      }

      await this.addPuzzleStylesheet();

      this.isGameInitialized = true;

      await this.$nextTick();

      $('.me').css({ 'background-image': `url(${ this.selectedImage })` });
      $('.pre_img li img').attr('src', this.selectedImage);
    },
    async gameOver (isSolved) {
      try {
        this.isGameFinished = true;
        this.isLoaderModalOpen = true;

        modals.setupCommonModal({ hideManually: true });

        const payload = {
          successful: isSolved,
          achievementName: `${this.addOnGameConfig.gameId}`,
          gameType: 'PUZZLE',
        };

        const { prizeValue } = await this.finishAddOnGame(payload);

        await this.getUserLoginDetails();

        this.postGameInfo.successful = isSolved;
        this.postGameInfo.prizeValue = prizeValue;

        await this.closeLoaderModalAsPromise();

        this.isPostGameModalOpen = true;

        modals.setupCommonModal({
          hideImmediate: true,
          afterHideCallback: () => {
            this.isPostGameModalOpen = false;
            this.postGameInfo.successful = false;
            this.postGameInfo.prizeValue = null;

            this.redirectToHomePage();
          },
        });
      } catch (_error) {
        await this.closeLoaderModalAsPromise();

        this.openMaintenanceModal();
      }
    },
    generateRandomizedTilesFromImage () {
      const cloneTilePositionList = [...originalTilePositionList];

      let randomPosition = null;

      for (let i = originalTilePositionList.length - 1; i >= 0; i--) {
        randomPosition = generateRandomNumberInRange(0, i);

        [cloneTilePositionList[i], cloneTilePositionList[randomPosition]] = [cloneTilePositionList[randomPosition], cloneTilePositionList[i]];
        this.shuffledTilePositionList[i] = cloneTilePositionList[i];
      }

      const elContainer = $('.box_puzzle');

      for (let i = 0; i < originalTilePositionList.length; i++) {
        const elClass = `me me_${ this.shuffledTilePositionList[i] } tile is_clickable`;
        const elStyle = `background-image: url("${ this.selectedImage }");`;

        elContainer.append(`<div class='${ elClass }' style='${ elStyle }' data-bid='${ this.shuffledTilePositionList[i] }' ></div>`);

        if (((i + 1) % 3) === 0) {
          elContainer.append('<br>');
        }
      }
    },
    registerTileClickListener () {
      let firstSelectedTileOriginalPosition = null;
      let secondSelectedTileOriginalPosition = null;
      let hasPendingTileSwap = false;

      const getCurrentTileOriginalPosition = (elSelector) => parseInt($(elSelector).attr('data-bid'), 10);

      const swapTilesInHtml = (firstElSelector, secondElSelector, firstTileOriginalPosition, secondTileOriginalPosition) => {
        const updateTileHtmlAttributes = (elSelector, currentOriginalPosition, targetOriginalPosition) => {
          $(elSelector)
            .addClass('me_' + targetOriginalPosition)
            .removeClass('me_' + currentOriginalPosition)
            .attr('data-bid', targetOriginalPosition);
        };

        updateTileHtmlAttributes(firstElSelector, firstTileOriginalPosition, secondTileOriginalPosition);
        updateTileHtmlAttributes(secondElSelector, secondTileOriginalPosition, firstTileOriginalPosition);
      };

      const swapTilesInShuffledList = (firstTileOriginalPosition, secondTileOriginalPosition) => {
        const firstTileShuffledPosition = this.shuffledTilePositionList.indexOf(firstTileOriginalPosition);
        const secondTileShuffledPosition = this.shuffledTilePositionList.indexOf(secondTileOriginalPosition);

        this.shuffledTilePositionList[firstTileShuffledPosition] = secondTileOriginalPosition;
        this.shuffledTilePositionList[secondTileShuffledPosition] = firstTileOriginalPosition;
      };

      const validateTile = (originalPosition) => {
        if (this.shuffledTilePositionList.indexOf(originalPosition) !== originalPosition) { return; }

        $(`.me_${ originalPosition }`).removeClass('is_clickable').addClass('correct_puzzle prevent_click');
      };

      const handleTiteClick = (targetEl) => {
        if (!hasPendingTileSwap) {
          firstSelectedTileOriginalPosition = getCurrentTileOriginalPosition(targetEl);

          $(`.me_${ firstSelectedTileOriginalPosition }`).css({ opacity: '.65' });

          hasPendingTileSwap = true;
        } else {
          secondSelectedTileOriginalPosition = getCurrentTileOriginalPosition(targetEl);

          $(`.me_${ firstSelectedTileOriginalPosition }`).css({ opacity: '1' });

          if (firstSelectedTileOriginalPosition !== secondSelectedTileOriginalPosition) {
            swapTilesInHtml(`.me_${ firstSelectedTileOriginalPosition }`, targetEl, firstSelectedTileOriginalPosition, secondSelectedTileOriginalPosition);

            swapTilesInShuffledList(firstSelectedTileOriginalPosition, secondSelectedTileOriginalPosition);
          }

          validateTile(firstSelectedTileOriginalPosition);
          if (firstSelectedTileOriginalPosition !== secondSelectedTileOriginalPosition) {
            validateTile(secondSelectedTileOriginalPosition);
          }

          firstSelectedTileOriginalPosition = null;
          secondSelectedTileOriginalPosition = null;
          hasPendingTileSwap = false;
        }

        if (this.isPuzzleSolved()) {
          if (this.$refs.timerRef.stopTimer) {
            this.$refs.timerRef.stopTimer();
          }

          this.gameOver(true);
        }
      };

      $('.me').click(function () {
        handleTiteClick(this);
      });
    },
    handleStartGame () {
      this.isGameInProgress = true;

      this.generateRandomizedTilesFromImage();

      if (this.$refs.timerRef.startTimer) {
        this.$refs.timerRef.startTimer();
      }

      this.registerTileClickListener();
    },
    handleTimeExpired () {
      this.gameOver(false);
    },
    handleGameMaintenanceRedirection ({ pagePath }) {
      modals.hideCommonModal({
        immediate: true,
        afterHideCallback: () => {
          this.isGameMaintenanceModalOpen = false;

          this.$router.push(pagePath);
        },
      });
    },
  },
  beforeDestroy () {
    this.resetAddOnGameConfig();

    this.removePuzzleStylesheet();
  },
};
</script>



// WEBPACK FOOTER //
// src/components/home/game/addOnGame/AddOnGamePuzzle.vue